import type { NextApiRequest } from "next";
import type { GetServerSidePropsContext } from "next";
import { GraphQLClient, gql } from "graphql-request";

export const IMPERSONATE_COOKIE = "cc.impersonate";

/** Sentinel token stored in session when impersonating (never a real JWT). */
export const IMPERSONATION_TOKEN = "__impersonating__";

/**
 * Browser-facing GraphQL endpoint that injects admin headers server-side.
 * Trailing slash is required: next.config has trailingSlash: true, and POSTs
 * without it get a 308 that drops the GraphQL body.
 */
export const IMPERSONATE_GRAPHQL_PATH = "/api/dev/impersonate-graphql/";
export const IMPERSONATE_SESSION_PATH = "/api/dev/impersonate-session/";
export const IMPERSONATE_START_PATH = "/api/dev/impersonate/";

/** Default College student Hasura role when role lookup is unavailable. */
export const DEFAULT_STUDENT_ROLE = "student";

export type ImpersonationTarget = {
  databaseId: string;
  cognitoId: string;
  /** Hasura role name from organization_role (usually "student"). */
  role?: string;
};

export function isImpersonationAllowed(): boolean {
  return process.env.ALLOW_IMPERSONATION === "true";
}

export function isImpersonationToken(token?: string | null): boolean {
  return token === IMPERSONATION_TOKEN;
}

export function organizationId(): string {
  return (
    process.env.NEXT_PUBLIC_ORGANIZATION_ID ||
    process.env.ORGANIZATION_ID ||
    ""
  );
}

/** Prefer NEXT_ADMIN_KEY; fall back to NEXT_API_SECRET. */
export function hasuraAdminSecret(): string {
  return process.env.NEXT_ADMIN_KEY || process.env.NEXT_API_SECRET || "";
}

/** Full admin - bypasses RLS. Only for bootstrap lookups (role / id checks). */
export function hasuraAdminHeaders(): Record<string, string> {
  return {
    "x-hasura-admin-secret": hasuraAdminSecret(),
    "x-hasura-organization-id": organizationId(),
  };
}

/**
 * Admin secret + role + user id = Hasura applies that role's permissions
 * (not full admin). This is how Console "run as user" works.
 */
export function hasuraImpersonationHeaders(
  target: ImpersonationTarget,
): Record<string, string> {
  return {
    "x-hasura-admin-secret": hasuraAdminSecret(),
    "x-hasura-role": target.role || DEFAULT_STUDENT_ROLE,
    "x-hasura-user-id": target.databaseId,
    "x-hasura-organization-id": organizationId(),
  };
}

export function buildImpersonationClaims(
  target: ImpersonationTarget,
): Record<string, string> {
  const role = target.role || DEFAULT_STUDENT_ROLE;
  return {
    "x-hasura-user-id": target.databaseId,
    "x-hasura-organization-id": organizationId(),
    "x-hasura-role": role,
    "x-hasura-default-role": role,
    "x-hasura-allowed-roles": role,
  };
}

const getUserRoleQuery = gql`
  query ImpersonationUserRole($id: uuid!) {
    user_by_pk(id: $id) {
      id
      organization
      role: organization_role {
        name
      }
    }
  }
`;

/** Resolve Hasura role for a database user (admin lookup). */
export async function fetchImpersonationRole(
  databaseId: string,
): Promise<string> {
  try {
    const client = new GraphQLClient(process.env.NEXT_PUBLIC_API_URL!, {
      headers: hasuraAdminHeaders(),
    });
    const result: any = await client.request(getUserRoleQuery, {
      id: databaseId,
    });
    const role = result?.user_by_pk?.role?.name;
    return typeof role === "string" && role.trim()
      ? role.trim()
      : DEFAULT_STUDENT_ROLE;
  } catch (err) {
    console.error("fetchImpersonationRole failed:", err);
    return DEFAULT_STUDENT_ROLE;
  }
}

function parseTarget(raw: unknown): ImpersonationTarget | null {
  if (!raw || typeof raw !== "object") return null;
  const databaseId = (raw as { databaseId?: unknown }).databaseId;
  const cognitoId = (raw as { cognitoId?: unknown }).cognitoId;
  const role = (raw as { role?: unknown }).role;
  if (typeof databaseId !== "string" || typeof cognitoId !== "string") {
    return null;
  }
  const db = databaseId.trim();
  const cognito = cognitoId.trim();
  if (!db || !cognito) return null;
  return {
    databaseId: db,
    cognitoId: cognito,
    role:
      typeof role === "string" && role.trim()
        ? role.trim()
        : undefined,
  };
}

function decodeCookieValue(value: string): ImpersonationTarget | null {
  try {
    return parseTarget(JSON.parse(decodeURIComponent(value)));
  } catch {
    try {
      return parseTarget(JSON.parse(value));
    } catch {
      return null;
    }
  }
}

/** Cookie target wins over env vars so you can switch students without restart. */
export function resolveImpersonationTarget(
  req?: Pick<NextApiRequest, "cookies"> | GetServerSidePropsContext["req"],
): ImpersonationTarget | null {
  if (!isImpersonationAllowed()) return null;

  const cookieValue = req?.cookies?.[IMPERSONATE_COOKIE];
  if (typeof cookieValue === "string" && cookieValue) {
    const fromCookie = decodeCookieValue(cookieValue);
    if (fromCookie) return fromCookie;
  }

  return parseTarget({
    databaseId: process.env.USER_DATABASE_ID,
    cognitoId: process.env.USER_COGNITO_ID,
    role: process.env.USER_HASURA_ROLE,
  });
}

/** Like resolveImpersonationTarget, but fills role via admin lookup when missing. */
export async function resolveImpersonationTargetWithRole(
  req?: Pick<NextApiRequest, "cookies"> | GetServerSidePropsContext["req"],
): Promise<ImpersonationTarget | null> {
  const target = resolveImpersonationTarget(req);
  if (!target) return null;
  if (target.role) return target;
  const role = await fetchImpersonationRole(target.databaseId);
  return { ...target, role };
}

export function encodeImpersonationCookie(target: ImpersonationTarget): string {
  return encodeURIComponent(
    JSON.stringify({
      databaseId: target.databaseId,
      cognitoId: target.cognitoId,
      ...(target.role ? { role: target.role } : {}),
    }),
  );
}

export function assertImpersonationSecret(
  secret: string | string[] | undefined,
) {
  const expected = process.env.IMPERSONATE_SECRET;
  if (!expected) {
    throw new Error("IMPERSONATE_SECRET is not configured");
  }
  const provided = Array.isArray(secret) ? secret[0] : secret;
  if (!provided || provided !== expected) {
    throw new Error("Invalid impersonation secret");
  }
}
